/* eslint-disable @typescript-eslint/no-explicit-any */
// Vendor
import * as tus from 'tus-js-client'

// Personal libs
import endpoints from 'src/shared/service/endpoint/endpoints.class';
import { apiPut, apiDelete, apiPost  } from './common/api';
import { IApiResponse } from '@shared/interface/api-response.interface';
import { ApiStatusEnum } from '@shared/enum/api-enum';
import { ApiError } from '@shared/error/api.error';
import { manageApiError } from './common/api.lib';
import { apiUpload, IApiUpload } from './common/api.upload';

/*****************************************
 *
 * PUT FUNCTIONS
 *
 *****************************************/
// Validate a video
export const apiPutValidateVideo = (
  videoId?: number,
): Promise<IApiResponse<string, string>> => {
  return apiPut(
    endpoints.video.putValidateVideo.addParams([videoId ?? 0]).getUrl(),
  )
    .then((response: IApiResponse<string, string>) => {
      if (
        response.returnStatus === ApiStatusEnum.SUCCESS &&
        ['VIDEO_VALIDATE_OK', 'VIDEO_VALIDATE_OK_NO_MAIL'].includes(
          response.returnCode,
        )
      ) {
        return response;
      } else {
        throw new ApiError(response.returnError ?? '', response.returnCode);
      }
    })
    .catch((error: ErrorConstructor) =>
      manageApiError(error, 'apiPutValidateVideo'),
    );
};

// Invalidate a video
export interface IApiPutInvalidateVideo {
  invalidationReason?: string;
  videoId?: number;
}
export const apiPutInvalidateVideo = (
  params: IApiPutInvalidateVideo,
): Promise<IApiResponse<string, string>> => {
  const payload = {
    invalidationReason: params.invalidationReason,
  };
  return apiPut(
    endpoints.video.putInvalidateVideo
      .addParams([params.videoId ?? 0])
      .getUrl(),
    payload,
  )
    .then((response: IApiResponse<string, string>) => {
      if (
        response.returnStatus === ApiStatusEnum.SUCCESS &&
        ['VIDEO_INVALIDATE_OK', 'VIDEO_INVALIDATE_OK_NO_MAIL'].includes(
          response.returnCode,
        )
      ) {
        return response;
      } else {
        throw new ApiError(response.returnError ?? '', response.returnCode);
      }
    })
    .catch((error: ErrorConstructor) =>
      manageApiError(error, 'apiPutInvalidateVideo'),
    );
};

// Ask to validate a video
export const apiPutAskToValidateVideo = (
  videoId?: number,
): Promise<IApiResponse<string, string>> => {
  return apiPut(
    endpoints.video.putAskToValidateVideo.addParams([videoId ?? 0]).getUrl(),
  )
    .then((response: IApiResponse<string, string>) => {
      if (
        response.returnStatus === ApiStatusEnum.SUCCESS &&
        ['VIDEO_PUBLISH_OK', 'VIDEO_PUBLISH_OK_NO_MAIL'].includes(
          response.returnCode,
        )
      ) {
        return response;
      } else {
        throw new ApiError(response.returnError ?? '', response.returnCode);
      }
    })
    .catch((error: ErrorConstructor) =>
      manageApiError(error, 'apiPutAskToValidateVideo'),
    );
};

/*****************************************
 *
 * POST FUNCTIONS
 *
 *****************************************/
// Post video
export interface IApiPostVideo {
  videoFile?: File;
  videoFileName?: string;
  videoFileSize?: number;
  videoFileExtension?: string;
  programId: number;
  mainUserId: number;
  secondaryUsersId?: number[];
  legacyVersion?: boolean;
  abortController?: AbortController
  csrf?: string
}
export const apiPostVideo = (
  params: IApiPostVideo,
): Promise<IApiResponse<string, string>> => {
  const endpointClass = endpoints.video.postVideo;
  return apiPost(endpointClass.getUrl(), params, {
    publicApi: endpointClass.isPublic(),
    abortController: params.abortController
  })
    .then((response: IApiResponse<string, string>) => {
      if (
        response.returnCode === 'VIDEO_UPLOAD_OK' &&
        response.returnStatus === ApiStatusEnum.SUCCESS
      ) {
        return response;
      } else {
        throw new ApiError(response.returnError ?? '', response.returnCode);
      }
    })
    .catch((error: ErrorConstructor) => manageApiError(error, 'apiPostVideo'));
};

export const apiUploadVideo = async (
  params: IApiUpload
): Promise<tus.Upload | undefined> => {
  const endpointClass = endpoints.video.uploadVideo;
  return apiUpload({
    ...params,
    api: endpointClass.getUrl(),
    isPublicApi: endpointClass.isPublic(),
  })
};

/*****************************************
 *
 * DELETE FUNCTIONS
 *
 *****************************************/
// Get UI configuration
export const apiDeleteVideo = (
  videoId?: number,
): Promise<IApiResponse<string, string>> => {
  return apiDelete(
    endpoints.video.deleteVideo.addParams([videoId ?? 0]).getUrl(),
  )
    .then((response: IApiResponse<string, string>) => {
      if (
        response.returnCode === 'VIDEO_DELETE_OK' &&
        response.returnStatus === ApiStatusEnum.SUCCESS
      ) {
        return response;
      } else {
        throw new ApiError(response.returnError ?? '', response.returnCode);
      }
    })
    .catch((error: ErrorConstructor) =>
      manageApiError(error, 'apiDeleteVideo'),
    );
};